export { getCsrfTokenFromCookie, installCsrfInjector } from "./csrf.js";
/**
 * Auth Handlers: the account's client (Phase 5b, step 4: the engine's).
 * Signing in, up and out, recovery, the session and the vault's key on this
 * device, the server's auth API. The screens are the interface's
 * (interfaces/classic/ui/auth/forms.js, interfaces/classic/ui/auth/screens.js).
 */

import { hasDocQuotaPending, discardQuotaPending } from '../services/quota-state.js';
import { signOutLosses } from './logout-warning.js';
import { accountCacheScope } from '../storage/account-cache.js';
import { E2EE } from "../crypto/e2ee.js";
import * as DeviceKey from "../crypto/device-key.js";
import { VaultStorage } from "../crypto/vault-storage.js";
import { isSyncAvailable, flushPendingWrites } from "../storage/index.js";
import { assertPlaintextPasswordAllowed, buildPasswordProof, rememberSecureLogin } from "./secure-login.js";
import { notify, ask, runOperation } from "../host.js";
import { NOTICE, QUESTION } from "../notices.js";
import { getLocale, t } from "../../shared/i18n/index.js";

// API base URL. Always relative — in Tauri online mode the storage
// adapter installs a window.fetch shim at startup that prefixes
// `/api/*`, `/auth/*`, etc. with the configured server origin (see
// installFetchProxy in engine/storage/tauri-adapter.js). On the web the
// fetch is unchanged and resolves to the same origin as the page.
const API_BASE = "";

// State (will be initialized by init())
let currentUser = null;
let masterKey = null;
let e2eeEnvelope = null;
let onAuthSuccess = null;
let onLogout = null;

// Track if session is expired (for "Login to sync" UI)
let sessionExpired = false;
// Set while this device asks the server to end its session: its own sockets
// then close with 4401 too, which is not "signed out elsewhere".
let signingOutHere = false;

// Session storage keys for master key persistence
const STORAGE_KEY_DATA = "orbital_mk_d";
const STORAGE_KEY_MASK = "orbital_mk_m";

// ============================================================================
// Logged-Out Mode Functions
// ============================================================================

/**
 * Get or create a persistent local user ID for logged-out mode.
 * Stored in localStorage so local data persists across sessions.
 */
export function getOrCreateLocalUser() {
  let localId = localStorage.getItem('orbital_local_user_id');
  if (!localId) {
    localId = `local-${crypto.randomUUID()}`;
    localStorage.setItem('orbital_local_user_id', localId);
  }
  return { user_id: localId, username: 'Local User' };
}

/**
 * Get or create a persistent local master key for logged-out mode.
 * This key encrypts local data in IndexedDB.
 * Stored in localStorage (XOR-scrambled) so local data remains accessible.
 * Scrambling prevents automated key-harvesting attacks from extracting
 * usable keys directly from localStorage dumps.
 */
export function getOrCreateLocalMasterKey() {
  const dataB64 = localStorage.getItem('orbital_local_mk_d');
  const maskB64 = localStorage.getItem('orbital_local_mk_m');
  if (dataB64 && maskB64) {
    return unscrambleKey(dataB64, maskB64);
  }

  // Migrate plaintext key from old storage format
  const legacyB64 = localStorage.getItem('orbital_local_master_key');
  let key;
  if (legacyB64) {
    key = Uint8Array.from(atob(legacyB64), c => c.charCodeAt(0));
    localStorage.removeItem('orbital_local_master_key');
  } else {
    key = crypto.getRandomValues(new Uint8Array(32));
  }

  const { data, mask } = scrambleKey(key);
  localStorage.setItem('orbital_local_mk_d', data);
  localStorage.setItem('orbital_local_mk_m', mask);
  return key;
}

/**
 * Clear local user data (used when local data is imported to real account).
 */
export function clearLocalUserData() {
  localStorage.removeItem('orbital_local_user_id');
  localStorage.removeItem('orbital_local_master_key'); // legacy
  localStorage.removeItem('orbital_local_mk_d');
  localStorage.removeItem('orbital_local_mk_m');
}

/**
 * Check if there's existing local user data that could be imported.
 */
export function hasLocalUserData() {
  return localStorage.getItem('orbital_local_user_id') !== null;
}

// ============================================================================
// User ID Cache (for session expiry recovery)
// ============================================================================

/**
 * Cache user info so we can recover CRDT data after session expiry.
 * Uses sessionStorage so it's cleared when browser closes.
 */
export function cacheUserId(userId, username = null) {
  if (userId) {
    sessionStorage.setItem('orbital_cached_user_id', userId);
  }
  if (username) {
    sessionStorage.setItem('orbital_cached_username', username);
  }
}

/**
 * Get cached user ID for session recovery.
 */
export function getCachedUserId() {
  return sessionStorage.getItem('orbital_cached_user_id');
}

/**
 * Get cached username for offline mode header display.
 */
export function getCachedUsername() {
  return sessionStorage.getItem('orbital_cached_username');
}

/**
 * Clear cached user info (on explicit logout).
 */
export function clearCachedUserId() {
  sessionStorage.removeItem('orbital_cached_user_id');
  sessionStorage.removeItem('orbital_cached_username');
}

// ============================================================================
// Master Key Storage (Session)
// ============================================================================

/**
 * Scramble master key for session storage
 * Uses XOR with a random mask - not cryptographically secure but
 * prevents casual copy-paste attacks
 */
function scrambleKey(key) {
  const mask = crypto.getRandomValues(new Uint8Array(key.length));
  const scrambled = new Uint8Array(key.length);
  for (let i = 0; i < key.length; i++) {
    scrambled[i] = key[i] ^ mask[i];
  }
  return {
    data: btoa(String.fromCharCode(...scrambled)),
    mask: btoa(String.fromCharCode(...mask)),
  };
}

/**
 * Unscramble master key from session storage
 */
function unscrambleKey(dataB64, maskB64) {
  const data = Uint8Array.from(atob(dataB64), (c) => c.charCodeAt(0));
  const mask = Uint8Array.from(atob(maskB64), (c) => c.charCodeAt(0));
  const key = new Uint8Array(data.length);
  for (let i = 0; i < data.length; i++) {
    key[i] = data[i] ^ mask[i];
  }
  return key;
}

/**
 * Store master key in session storage (scrambled)
 *
 * Two layers, deliberately:
 *  - sessionStorage is the in-tab cache. It is read synchronously all over the
 *    app (main.js boot, sync-orchestrator), so it has to stay sync.
 *  - the device key (IndexedDB, non-extractable AES-GCM) is the layer that
 *    survives closing the browser. It is async, so it is written fire-and-forget
 *    here and read once at boot to refill sessionStorage.
 *
 * `userId` is optional only because two internal callers store the key before
 * `currentUser` is assigned; they pass it explicitly. Without an id we skip
 * device persistence rather than storing a key we cannot match to an account on
 * the way back out.
 */
function storeMasterKey(key, userId = currentUser?.user_id || null) {
  const { data, mask } = scrambleKey(key);
  sessionStorage.setItem(STORAGE_KEY_DATA, data);
  sessionStorage.setItem(STORAGE_KEY_MASK, mask);
  // Key stored (avoid logging specifics to prevent information disclosure)

  if (userId) {
    // Not awaited: a slow or unavailable IndexedDB must not delay sign-in, and
    // persistMasterKey already swallows its own failures.
    DeviceKey.persistMasterKey(userId, key).catch(() => {});
  }
}

/**
 * Retrieve master key from session storage
 */
export function retrieveMasterKey() {
  const data = sessionStorage.getItem(STORAGE_KEY_DATA);
  const mask = sessionStorage.getItem(STORAGE_KEY_MASK);
  if (!data || !mask) return null;
  try {
    const key = unscrambleKey(data, mask);
    // Key retrieved successfully
    return key;
  } catch (e) {
    console.error("[Auth] Failed to unscramble master key");
    clearStoredMasterKey();
    return null;
  }
}

/**
 * Clear stored master key from session storage AND from device storage.
 *
 * Both layers go together. Leaving the device copy behind would let the next
 * page load silently restore a key the user just signed out of.
 */
function clearStoredMasterKey() {
  sessionStorage.removeItem(STORAGE_KEY_DATA);
  sessionStorage.removeItem(STORAGE_KEY_MASK);
  DeviceKey.clearPersistedMasterKey().catch(() => {});
}

/**
 * Refill sessionStorage from the device key, if this browser has one stored.
 *
 * Called once at boot, before anything reads retrieveMasterKey(). Everything
 * downstream is unchanged: it still finds the key in sessionStorage exactly as
 * it would have after a password login, so no other caller needs to become
 * async.
 *
 * Returns false when nothing was restored, which simply means the user is asked
 * for their password as before.
 *
 * @param {string|null} expectedUserId - account the session cookie belongs to
 * @returns {Promise<boolean>}
 */
export async function rehydrateMasterKeyFromDevice(expectedUserId = null) {
  // A key already in sessionStorage is fresher by definition (same tab, same
  // session) — don't overwrite it.
  if (sessionStorage.getItem(STORAGE_KEY_DATA)) return true;

  const key = await DeviceKey.restoreMasterKey(expectedUserId);
  if (!key) return false;

  const { data, mask } = scrambleKey(key);
  sessionStorage.setItem(STORAGE_KEY_DATA, data);
  sessionStorage.setItem(STORAGE_KEY_MASK, mask);
  key.fill(0);
  console.log("[Auth] Master key restored from device");
  return true;
}

/**
 * Persistent-login controls, re-exported so UI layers don't reach into the
 * crypto module directly.
 */
export const deviceLogin = {
  isEnabled: DeviceKey.isPersistenceEnabled,
  setEnabled: DeviceKey.setPersistenceEnabled,
  hasStoredKey: DeviceKey.hasPersistedKey,
  forgetDevice: DeviceKey.forgetDevice,
};

/**
 * Initialize auth handlers with callbacks
 */
export function init(callbacks = {}) {
  onAuthSuccess = callbacks.onAuthSuccess;
  onLogout = callbacks.onLogout;
}

/**
 * API helper with cookie-based session management.
 *
 * In runtimes where the storage adapter has disabled sync (Tauri
 * local-only mode), every endpoint is unreachable. Fail fast with a
 * `localOnly` flag rather than waiting for a TCP timeout — every call
 * site already wraps `api()` in try/catch for the offline case, so the
 * thrown error routes naturally into the logged-out path.
 */
export async function api(endpoint, method = "GET", body = null) {
  if (!isSyncAvailable()) {
    const error = new Error(t("account.error.localOnly"));
    error.status = 0;
    error.localOnly = true;
    throw error;
  }

  const headers = {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    method,
    headers,
    credentials: "include", // Include session cookies
    body: body ? JSON.stringify(body) : null,
  });

  if (!response.ok) {
    // Try to parse error body for better error messages
    let errorMessage = t("account.error.requestFailed", { status: String(response.status) });
    let code = null;
    let retryAfter = null;
    try {
      const data = await response.json();
      errorMessage = data.error || data.detail || errorMessage;
      code = typeof data.code === "string" ? data.code : null;
      retryAfter = Number.isFinite(data.retry_after) ? data.retry_after : null;
    } catch {
      // Body wasn't JSON, use default message
    }
    // Too many failed sign-ins from this address: say how long, in the user's language.
    if (code === "too_many_attempts") {
      errorMessage = t("account.error.tooManyAttempts", { minutes: String(Math.max(1, Math.ceil((retryAfter || 60) / 60))) });
    }

    if (response.status === 401) {
      currentUser = null;
    }

    const error = new Error(errorMessage);
    error.status = response.status;
    error.code = code;
    error.retryAfter = retryAfter;
    throw error;
  }

  return response.json();
}

/**
 * Validate password against security requirements
 */
function validatePassword(password) {
  const errors = [];
  if (password.length < 8) {
    errors.push(t("account.password.minLength"));
  }
  if (!/[0-9]/.test(password)) {
    errors.push(t("account.password.number"));
  }
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push(t("account.password.special"));
  }
  if (!/[a-z]/.test(password)) {
    errors.push(t("account.password.lowercase"));
  }
  if (!/[A-Z]/.test(password)) {
    errors.push(t("account.password.uppercase"));
  }
  return errors;
}

/** The unmet password rules as one list, for the "must have" messages. */
function passwordRequirements(errors) {
  return errors.join(t("account.password.separator"));
}

// ============================================================================
// Login keys — the password never leaves this device
// ============================================================================
//
// The vault KEK is Argon2id(password, salt_p); anyone holding the password can
// unwrap the master key. So the server only ever receives
// authKey = HKDF(KEK) (see E2EE.deriveAuthKey), fetched salt_p first via
// /api/auth/prelogin. Accounts created before this scheme are "legacy": their
// stored hash is of the plaintext password, so their next login sends it
// together with the authKey, and once this device has unwrapped the vault with
// the same KEK, /api/auth/upgrade switches the account over (sending the
// password one last time). The downgrade guard (secure-login.js) refuses to
// send the password of an account this device has seen upgraded.

/**
 * Derive this account's vault KEK and login key from the password.
 * Caller must zero `kekP` and `authKey`.
 * @returns {Promise<{kekP: Uint8Array, authKey: Uint8Array, saltP: string, legacy: boolean}>}
 */
async function deriveLoginMaterial(identifier, password) {
  const pre = await api("/api/auth/prelogin", "POST", { username: identifier });
  await E2EE.loadArgon2();
  const { kekP, authKey } = await E2EE.deriveLoginKeys(password, E2EE.fromBase64(pre.salt_p));
  return { kekP, authKey, saltP: pre.salt_p, legacy: pre.auth_scheme === "legacy" };
}

/**
 * The signed-in account's scheme as the server reports it now. A tab opened
 * before the account was upgraded (here or on another device) still holds
 * "legacy" in currentUser; asking again keeps it from sending a password the
 * server no longer needs.
 * @returns {Promise<{scheme: string, identifiers: string[]}>}
 */
async function freshAuthScheme() {
  const me = await api("/api/auth/me");
  const identifiers = [me.username, me.email, currentUser?.username, currentUser?.email];
  if (currentUser) currentUser = { ...currentUser, auth_scheme: me.auth_scheme };
  rememberSecureLogin(me);
  return { scheme: me.auth_scheme, identifiers };
}

/**
 * Store the login key of a legacy account, once this device has unwrapped the
 * vault with the KEK it comes from (a key derived from an envelope it could
 * not open would lock the account out). Not fatal: on failure the account
 * stays legacy and the next sign-in tries again.
 */
async function upgradeLoginKey(authKey, password, saltP) {
  const user = currentUser;
  try {
    assertPlaintextPasswordAllowed([user.username, user.email]);
    const result = await api("/api/auth/upgrade", "POST", {
      auth_key: E2EE.toBase64(authKey),
      salt_p: saltP,
      password,
    });
    currentUser = { ...user, auth_scheme: result.auth_scheme };
    rememberSecureLogin(currentUser);
  } catch (err) {
    currentUser = currentUser || user;
    console.warn("[Auth] Secure sign-in upgrade postponed:", err.message);
  }
}

/**
 * Proof of the current password for re-authenticated actions (recovery-phrase
 * regeneration, account deletion, identity repair): the login key, plus the
 * password itself only while the server still reports the account as legacy
 * and this device never saw it upgraded.
 *
 * The password is checked locally first (the same KEK must unwrap wrap_p), so
 * a wrong password fails before anything irreversible or anything is sent.
 *
 * @param {string} password
 * @param {object|null} envelope  current envelope, fetched when not given
 */
export async function passwordProof(password, envelope = null) {
  envelope = envelope || await api("/api/e2ee/envelope");
  await E2EE.loadArgon2();
  const { kekP, authKey } = await E2EE.deriveLoginKeys(password, E2EE.fromBase64(envelope.salt_p));
  try {
    (await E2EE.unwrapWithKek(envelope, kekP)).fill(0);
  } catch {
    authKey.fill(0);
    throw new Error(t("account.password.incorrect"));
  } finally {
    kekP.fill(0);
  }
  try {
    const { scheme, identifiers } = await freshAuthScheme();
    return buildPasswordProof({ authKey: E2EE.toBase64(authKey), password, scheme, identifiers });
  } finally {
    authKey.fill(0);
  }
}

// ============================================================================
// Signing in, signing up, recovery: the account's client. The screens that
// use it (forms, the unlock and recovery-phrase dialogs) are the interface's
// (interfaces/classic/ui/auth/forms.js). Each reports its steps through `onStage`; errors are
// thrown with a message in the person's language.
// ============================================================================

/**
 * What the new password lacks (the server never sees it, so cannot check
 * it), as one message; null when it meets the rules.
 * @param {string} password
 * @returns {string|null}
 */
export function passwordProblem(password) {
  const errors = validatePassword(password);
  return errors.length > 0 ? t("account.password.mustHave", { requirements: passwordRequirements(errors) }) : null;
}

/**
 * Sign in with a password; the vault's key is unwrapped here (the password
 * never leaves this device). Then `completeSignIn`.
 *
 * @param {string} username - the name or the email
 * @param {string} password
 * @param {{onStage?: (stage: "signing-in"|"unlocking") => void}} [options]
 * @returns {Promise<"signed-in"|"needs-recovery-phrase">} needs-recovery-phrase:
 *   signed in, but the password does not open the vault (`unlockWithRecoveryPhrase`)
 */
export async function signIn(username, password, { onStage = () => {} } = {}) {
  onStage("signing-in");
  let login;
  try {
    login = await deriveLoginMaterial(username, password);
  } catch (err) {
    // fetch() rejects with a TypeError when the server is unreachable.
    throw new Error(err instanceof TypeError ? t("account.error.unreachable") : err.message);
  }
  const { kekP, authKey, saltP, legacy } = login;
  try {
    const body = { username, auth_key: E2EE.toBase64(authKey) };
    if (legacy) {
      // The server still holds a hash of the plaintext password.
      assertPlaintextPasswordAllowed([username]);
      body.password = password;
    }
    let data;
    try {
      data = await api("/api/auth/login", "POST", body);
    } catch (err) {
      // An email signs in only once verified (the server answers as for an
      // unknown name, so as not to reveal it): say so when one was used.
      if (err?.status === 401 && username.includes("@") && err.message === "Invalid username/email or password") {
        err.message = `${err.message} ${t("account.login.emailNeedsVerified")}`;
      }
      throw err;
    }
    currentUser = data;
    rememberSecureLogin(data);

    // Try to fetch and unlock E2EE envelope
    onStage("unlocking");
    try {
      e2eeEnvelope = await api("/api/e2ee/envelope");

      // Try to unwrap master key with password
      try {
        // Reuse the KEK derived for the login key (one Argon2id run) unless
        // salt_p changed in between (password changed on another device).
        masterKey = e2eeEnvelope.salt_p === saltP
          ? await E2EE.unwrapWithKek(e2eeEnvelope, kekP)
          : await E2EE.unwrapWithPassword(e2eeEnvelope, password);
        storeMasterKey(masterKey);
      } catch (unwrapErr) {
        // Password didn't work - need recovery phrase
        console.log("[Auth] Password unlock failed, showing recovery UI");
        return "needs-recovery-phrase";
      }
      // The vault opened with the KEK behind authKey: a legacy account can
      // now store that login key.
      if (legacy && currentUser.auth_scheme === "legacy" && e2eeEnvelope.salt_p === saltP) {
        await upgradeLoginKey(authKey, password, saltP);
      }
    } catch (envelopeErr) {
      // No envelope exists - E2EE is required: signed out again.
      console.error("[Auth] No E2EE envelope found:", envelopeErr.message);
      await api("/api/auth/logout", "POST");
      currentUser = null;
      throw new Error(t("account.login.noEnvelope"));
    }
  } finally {
    kekP.fill(0);
    authKey.fill(0);
  }
  return "signed-in";
}

/**
 * Open the vault with the recovery phrase, after `signIn` answered
 * "needs-recovery-phrase". Then `completeSignIn`.
 * @param {string} mnemonic
 */
export async function unlockWithRecoveryPhrase(mnemonic) {
  if (!await E2EE.validateMnemonic(mnemonic)) throw new Error(t("account.phrase.invalid"));
  try {
    masterKey = await E2EE.unwrapWithRecovery(e2eeEnvelope, mnemonic);
    storeMasterKey(masterKey);
  } catch {
    throw new Error(t("account.unlock.failed"));
  }
}

/**
 * Create an account: its keys are made here (the password is never sent).
 * Resolves with the recovery phrase to show; then `completeSignIn({ isNewUser: true })`.
 *
 * @param {{username: string, email?: string|null, password: string}} account
 * @param {{onStage?: (stage: "generating-keys"|"creating") => void}} [options]
 * @returns {Promise<string>} the recovery phrase (12 words)
 */
export async function register({ username, email = null, password }, { onStage = () => {} } = {}) {
  const problem = passwordProblem(password);
  if (problem) throw new Error(problem);
  // '@' is reserved for email addresses (the server refuses it too).
  if (username.includes("@")) throw new Error(t("account.signup.usernameAt"));
  // Basic email validation if provided
  if (email && !email.includes("@")) throw new Error(t("account.signup.invalidEmail"));

  // Generate E2EE keys first: the login key is derived from the envelope's
  // salt_p, and the server stores both atomically with the new account.
  onStage("generating-keys");
  await E2EE.loadArgon2();
  const newMasterKey = E2EE.generateMasterKey();
  const mnemonic = await E2EE.generateMnemonic();
  const { envelope, authKey } = await E2EE.createEnvelopeWithAuthKey(password, mnemonic, newMasterKey);

  // Register with server (email is optional). The password is never sent.
  onStage("creating");
  // The app's language at sign-up: the account's emails are written in it.
  const registerData = { username, auth_key: E2EE.toBase64(authKey), envelope, locale: getLocale() };
  authKey.fill(0);
  if (email) {
    registerData.email = email;
  }
  const data = await api("/api/auth/register", "POST", registerData);
  currentUser = data;
  rememberSecureLogin(data);
  masterKey = newMasterKey;
  storeMasterKey(masterKey);
  e2eeEnvelope = envelope;

  // Cache user ID for session expiry recovery
  cacheUserId(currentUser.user_id, currentUser.username);
  return mnemonic;
}

/**
 * Recover an account with its recovery phrase and choose a new password.
 * Then `completeSignIn`.
 *
 * @param {{username: string, mnemonic: string, newPassword: string}} recovery
 * @param {{onStage?: (stage: "fetching"|"decrypting"|"reencrypting"|"updating") => void}} [options]
 */
export async function recover({ username, mnemonic, newPassword }, { onStage = () => {} } = {}) {
  const problem = passwordProblem(newPassword);
  if (problem) throw new Error(problem);
  // Validate mnemonic format
  if (!await E2EE.validateMnemonic(mnemonic)) throw new Error(t("account.phrase.invalid"));

  // Step 1: Get recovery envelope from server
  onStage("fetching");
  const recoveryEnvelope = await api("/api/auth/recovery-envelope", "POST", {
    username,
  });

  // Step 2: Load Argon2 and decrypt master key using recovery phrase
  onStage("decrypting");
  await E2EE.loadArgon2();

  let decryptedMasterKey;
  try {
    decryptedMasterKey = await E2EE.unwrapWithRecovery(
      recoveryEnvelope,
      mnemonic,
    );
  } catch (decryptErr) {
    throw new Error(t("account.recover.decryptFailed"));
  }

  // Step 3: Re-wrap master key with new password (and derive the new login key)
  onStage("reencrypting");
  const newSaltP = E2EE.generateSalt();
  const { kekP: newKekP, authKey: newAuthKey } = await E2EE.deriveLoginKeys(newPassword, newSaltP);
  const newWrapP = await E2EE.encrypt(newKekP, decryptedMasterKey);

  // C-1: Compute recovery verifier to prove phrase knowledge to server.
  // Re-derive kekR to compute SHA-256(kekR). This proves we know the recovery phrase.
  const saltR = E2EE.fromBase64(recoveryEnvelope.salt_r);
  const mnemonicSeed = await E2EE.mnemonicToSeed(mnemonic);
  const kekR = await E2EE.deriveKey(
    E2EE.toBase64(mnemonicSeed.slice(0, 32)),
    saltR,
  );
  const recoveryVerifier = await E2EE.sha256(kekR);

  // Step 4: Submit to server
  onStage("updating");
  const result = await api("/api/auth/recover", "POST", {
    username,
    new_auth_key: E2EE.toBase64(newAuthKey),
    salt_p: E2EE.toBase64(newSaltP),
    kdf_params_p: E2EE.ARGON2_PARAMS,
    wrap_p: E2EE.toBase64(newWrapP),
    recovery_verifier: E2EE.toBase64(recoveryVerifier),
  });

  // Zero intermediate key material (defense-in-depth)
  newKekP.fill(0);
  newAuthKey.fill(0);
  kekR.fill(0);
  mnemonicSeed.fill(0);
  recoveryVerifier.fill(0);

  // Success! Store master key and proceed
  currentUser = result;
  rememberSecureLogin(result);
  masterKey = decryptedMasterKey;
  storeMasterKey(masterKey);
  e2eeEnvelope = {
    salt_p: E2EE.toBase64(newSaltP),
    kdf_params_p: E2EE.ARGON2_PARAMS,
    wrap_p: E2EE.toBase64(newWrapP),
    salt_r: recoveryEnvelope.salt_r,
    kdf_params_r: recoveryEnvelope.kdf_params_r,
    wrap_r: recoveryEnvelope.wrap_r,
  };
}

/**
 * Signed in (a password, a recovery phrase, a new account): the app starts
 * on this account (the callback registered with `init`).
 * @param {{isNewUser?: boolean}} [options]
 */
export async function completeSignIn({ isNewUser = false } = {}) {
  // Clear session expired state
  sessionExpired = false;
  // Cache user ID for session expiry recovery
  cacheUserId(currentUser.user_id, currentUser.username);
  if (onAuthSuccess) {
    await onAuthSuccess({ user: currentUser, masterKey, e2eeEnvelope, ...(isNewUser ? { isNewUser: true } : {}) });
  }
}

/**
 * Sign this device in again without leaving the page (interfaces/classic/ui/session-resume.js):
 * the server no longer accepts its session (the cookie expired, or the
 * session was ended elsewhere), and the password gets a new one for the same
 * account. The vault key in memory stays (a password change does not change
 * it), nothing is reloaded; the sockets, which keep retrying, then connect
 * and send the changes made meanwhile. Another account is refused (signed out
 * again at once), so two accounts' data never meet on one page.
 * @param {string} password
 */
export async function resumeSession(password) {
  const userId = currentUser?.user_id || getCachedUserId();
  const username = currentUser?.username || getCachedUsername();
  if (!userId || !username) throw new Error(t("account.resume.noAccount"));
  let login;
  try {
    login = await deriveLoginMaterial(username, password);
  } catch (err) {
    throw new Error(err instanceof TypeError ? t("account.error.unreachable") : err.message);
  }
  const { kekP, authKey, legacy } = login;
  try {
    const body = { username, auth_key: E2EE.toBase64(authKey) };
    if (legacy) {
      assertPlaintextPasswordAllowed([username]);
      body.password = password;
    }
    let data;
    try {
      data = await api("/api/auth/login", "POST", body);
    } catch (err) {
      if (err.code !== "too_many_attempts") throw err;
      // The dialog waits with its button disabled; working on goes on meanwhile.
      const wait = new Error(t("account.resume.tooManyAttempts", { minutes: String(Math.max(1, Math.ceil((err.retryAfter || 60) / 60))) }));
      wait.retryAfterMs = (err.retryAfter || 60) * 1000;
      throw wait;
    }
    if (data.user_id !== userId) {
      await api("/api/auth/logout", "POST").catch(() => {});
      throw new Error(t("account.resume.otherAccount", { username }));
    }
    currentUser = { ...(currentUser || {}), ...data };
    rememberSecureLogin(data);
    sessionExpired = false;
    cacheUserId(data.user_id, data.username);
    return data;
  } finally {
    kekP.fill(0);
    authKey.fill(0);
  }
}

/** Whether this device holds the vault's key (signed in, or its key kept here). */
export function hasVaultKey() {
  return !!(masterKey || retrieveMasterKey());
}

/**
 * Replace the account's recovery phrase (signed in), with the current
 * password (the screen: interfaces/classic/ui/auth/forms.js regenerateRecoveryPhrase).
 *
 * The server only accepts a new recovery half together with proof of the
 * password, so a stolen session cannot plant a recovery phrase of its own
 * and then reset the password with it. A fresh 12-word phrase re-wraps the
 * in-memory master key for recovery, and the envelope is PUT with the
 * existing PASSWORD wrapping sent back verbatim (the server refuses any
 * change to it here). The master key is unchanged, so both the password and
 * the new phrase still decrypt it — no data loss.
 *
 * This is the remediation for legacy accounts with no `recovery_verifier`
 * (created before that column existed): `/auth/recover` rejects verifier-less
 * accounts as account-takeover hardening, and running this once restores
 * phrase recovery. It also lets any user rotate a lost/compromised phrase.
 *
 * @param {string} password
 * @returns {Promise<string>} the new phrase
 */
export async function replaceRecoveryPhrase(password) {
  const key = masterKey || retrieveMasterKey();
  if (!key) {
    throw new Error(t("account.phrase.loginRequired"));
  }
  // Fetch the authoritative current envelope so the password wrapping we
  // re-send is exactly what's stored — never reconstruct or guess it.
  const current = await api("/api/e2ee/envelope");
  if (!current || !current.salt_p || !current.wrap_p || !current.kdf_params_p) {
    throw new Error(t("account.phrase.noPasswordWrap"));
  }
  const proof = await passwordProof(password, current);

  const mnemonic = await E2EE.generateMnemonic();
  const envelope = await E2EE.regenerateRecoveryEnvelope(current, mnemonic, key);

  await api("/api/e2ee/envelope", "PUT", { ...envelope, ...proof });

  // Keep the in-memory envelope in sync with what the server now stores. The
  // account now has a recovery_verifier, so reflect that (matches the server's
  // has_recovery_verifier on the next fetch) — this stops the upgrade prompt
  // from re-appearing.
  e2eeEnvelope = { ...envelope, has_recovery_verifier: true };
  return mnemonic;
}

/**
 * Change the account password.
 *
 * E2EE detail that makes this cheap: the master key never changes. The password
 * only ever wraps it, so a change re-derives a KEK from the new password and
 * re-wraps the same key. Nothing needs re-encrypting and nothing re-syncs.
 *
 * The flip side of that is why signing other devices out has to be an explicit
 * server-side act: their stored master keys remain perfectly valid, so nothing
 * about re-wrapping the envelope would lock them out on its own. The server
 * therefore deletes every session — always, not as an option — and this device
 * leaves with a freshly issued one.
 *
 * The current password is verified locally first, by unwrapping the envelope
 * with it. That means a wrong password fails before anything is sent, and the
 * server still checks it again independently.
 *
 * @param {string} currentPassword
 * @param {string} newPassword
 * @returns {Promise<void>}
 */
export async function changePassword(currentPassword, newPassword) {
  if (!currentUser) throw new Error(t("account.error.notSignedIn"));
  // The server never sees the new password, so it can't check its strength:
  // enforce the same rules as registration here.
  const passwordErrors = validatePassword(newPassword || "");
  if (passwordErrors.length > 0) {
    throw new Error(t("account.password.newMustHave", { requirements: passwordRequirements(passwordErrors) }));
  }

  await E2EE.loadArgon2();

  // Always work from the server's current envelope rather than the cached one,
  // so a change made on another device since this page loaded is not clobbered.
  const envelope = await api("/api/e2ee/envelope");
  if (!envelope) throw new Error(t("account.changePassword.noEnvelope"));

  // Verify the current password by actually unwrapping with it. The same
  // Argon2id output yields the current login key the server checks.
  const current = await E2EE.deriveLoginKeys(currentPassword, E2EE.fromBase64(envelope.salt_p));
  let unwrappedMasterKey;
  try {
    unwrappedMasterKey = await E2EE.unwrapWithKek(envelope, current.kekP);
  } catch {
    current.authKey.fill(0);
    throw new Error(t("account.password.currentIncorrect"));
  } finally {
    current.kekP.fill(0);
  }

  // Re-wrap the SAME master key under a KEK derived from the new password.
  const newSaltP = E2EE.generateSalt();
  const { kekP: newKekP, authKey: newAuthKey } = await E2EE.deriveLoginKeys(newPassword, newSaltP);
  const newWrapP = await E2EE.encrypt(newKekP, unwrappedMasterKey);

  try {
    const body = {
      current_auth_key: E2EE.toBase64(current.authKey),
      new_auth_key: E2EE.toBase64(newAuthKey),
      salt_p: E2EE.toBase64(newSaltP),
      kdf_params_p: E2EE.ARGON2_PARAMS,
      wrap_p: E2EE.toBase64(newWrapP),
    };
    // Accounts not yet upgraded can only be verified with the password itself
    // (last time it is sent — this change moves them to login keys). Ask the
    // server for the scheme now, and never for an account this device saw
    // upgraded (see secure-login.js).
    const { scheme, identifiers } = await freshAuthScheme();
    if (scheme === "legacy") {
      assertPlaintextPasswordAllowed(identifiers);
      body.current_password = currentPassword;
    }
    await api("/api/auth/change-password", "POST", body);
    currentUser = { ...currentUser, auth_scheme: "client_kdf_v1" };
    rememberSecureLogin(currentUser);
  } finally {
    // Zero intermediate key material whether or not the call succeeded.
    newKekP.fill(0);
    newAuthKey.fill(0);
    current.authKey.fill(0);
  }

  // Keep the in-memory envelope in step with what the server now holds, so a
  // later recovery-phrase regeneration copies the new password half rather than
  // resurrecting the old one.
  e2eeEnvelope = {
    ...envelope,
    salt_p: E2EE.toBase64(newSaltP),
    kdf_params_p: E2EE.ARGON2_PARAMS,
    wrap_p: E2EE.toBase64(newWrapP),
  };

  // The master key is unchanged, but the server rotated our session cookie.
  // Re-persist so the device making the change stays signed in — it is the one
  // device the eviction deliberately spares.
  masterKey = unwrappedMasterKey;
  storeMasterKey(masterKey, currentUser.user_id);
}

/**
 * Logout - Save device data, clear account state and reopen the local workspace
 */
/**
 * Sign out: this device's session only (the account's other devices stay
 * signed in), or with `everywhere`, every session of the account (the
 * profile's "Sign out on all devices"). Either way this device's account
 * data is removed as below.
 */
/**
 * Sign out of this device (or, `everywhere`, of every device).
 * `confirmed`: the caller already asked "sign out?" (the mobile sheet,
 * "Sign out everywhere"); a list of what would be lost is still shown.
 */
export async function logout({ everywhere = false, confirmed = false } = {}) {
  // A field being typed in saves on blur, here or in another interface's
  // frame (same origin), before the pending writes are flushed.
  for (const frame of document.querySelectorAll("iframe")) {
    try { frame.contentDocument?.activeElement?.blur?.(); } catch { /* another origin */ }
  }
  document.activeElement?.blur();
  try { await flushPendingWrites(); }
  catch (error) {
    console.error('[Auth] Logout cancelled because a local save failed:', error);
    notify({ code: NOTICE.SAVE_FAILED, level: 'error', message: t('account.logout.saveFailed'), data: { before: 'sign-out' } });
    return;
  }
  const [maps, vault, shares, health, pauses, waiting, unsynced, attachments, dataExport] = await Promise.all([
    import('../sync/crdt-bridge.js'), import('../sync/user-crdt-bridge.js'), import('../sync/share-lifecycle.js'),
    import('../sync/storage-health.js'), import('../sync/sync-pause.js'),
    import('../services/waiting-attachments.js'), import('../sync/unsynced-changes.js'),
    import('../services/attachment-service.js'), import('../crypto/data-export.js'),
  ]);
  const docs = VaultStorage.getIndex()?.docs || [];
  const vaultId = vault.getOnlineVaultDocId();
  // One last sync: what is not on the server yet is sent, and confirmed.
  // Then what signing out would lose is named, one by one (the dirty bits:
  // engine/sync/unsynced-changes.js). Nothing is kept for later.
  const reachable = navigator.onLine !== false && await checkServerHealth();
  let losses;
  try {
    losses = await runOperation(t('account.signOut.sendingTitle'), async () => {
      if (reachable) {
        // Closed maps with changes the server lacks: opened briefly, sent.
        const live = new Set([maps.getCurrentDocId?.(), ...(maps.getActiveLinkedDocs?.() || [])].filter(Boolean));
        const closed = docs.filter((doc) => doc._storage === 'online' && doc.role !== 'viewer'
          && unsynced.unsyncedState(doc.docId) === 'dirty' && !live.has(doc.docId) && !shares.hasSharedDoc?.(doc.docId))
          .map((doc) => doc.docId);
        const { sendClosedMapChanges } = await import('../sync/send-closed-maps.js');
        await Promise.all([
          maps.settlePendingWrites?.(10000).catch(() => false),
          shares.settlePendingWrites(10000).catch(() => false),
          vault.settlePendingWrites(10000).catch(() => false),
          attachments.sendWaitingAttachments().catch(() => null),
          sendClosedMapChanges(closed, { timeoutMs: 15000 }).catch(() => null),
        ]);
      }
      const paused = pauses.getSyncPauses();
      return signOutLosses({
        docs,
        stateOf: unsynced.unsyncedState,
        vaultId,
        vaultConfirmed: reachable && vault.getConnectionState() === 'synced' && !vault.hasPendingWrites(),
        checkWithServer: reachable ? (ids) => dataExport.checkOnlineCopiesBeforeSignOut(ids) : null,
        hasCopy: async (docId) => !!(await dataExport.readLocalMapState(docId).catch(() => ({ state: true }))).state,
        quotaRefused: hasDocQuotaPending,
        paused: (docId) => paused.some((pause) => pause.scope === 'map' && pause.id === docId),
        waitingFiles: await waiting.waitingFileNames(),
        storageBroken: !health.isHealthy(),
      });
    }, (lines) => (lines.length ? [t('account.signOut.notAllSent'), true] : [t('account.signOut.allSent')]));
  } catch (error) {
    console.error('[Auth] Sign-out could not check what is on the server:', error);
    notify({ code: NOTICE.SIGN_OUT_CHECK_FAILED, level: 'error', message: t('account.signOut.checkFailed') });
    return;
  }
  // The desktop app always says what signing out removes from the computer.
  const desktop = typeof window !== 'undefined' && !!window.__TAURI_INTERNALS__;
  const intro = desktop ? `${t('account.logout.desktopMessage')} ` : '';
  // Nothing lost: still asked (a click on the wrong button must not sign
  // out), unless the caller just did; the desktop always explains.
  const dialog = losses.length
    ? { title: t('account.signOut.lossTitle'), message: `${intro}${t('account.signOut.lossMessage')}`, details: losses,
      confirmText: t('account.signOut.lossConfirm'), cancelText: t('common.cancel'), confirmClass: 'danger' }
    : desktop
      ? { title: t('account.logout.desktopTitle'), message: t('account.logout.desktopMessage'), details: [],
        confirmText: t('account.logout.confirm'), cancelText: t('common.cancel') }
      : confirmed ? null
        : { title: t('account.logout.webTitle'), message: t('account.logout.webMessage'), details: [],
          confirmText: t('account.logout.confirm'), cancelText: t('common.cancel') };
  if (dialog && !await ask({ code: QUESTION.CONFIRM, data: dialog, fallback: false })) return;
  // The account's data on this device: every online map's copy, its vault.
  const cacheScope = accountCacheScope(currentUser?.user_id, docs);
  unsynced.clearUnsynced();
  let serverConfirmed = false;
  signingOutHere = true;
  try {
    await api(everywhere ? "/api/auth/logout-all" : "/api/auth/logout", "POST");
    serverConfirmed = true;
  } catch (err) {
    // Ignore logout errors
    console.error("[Auth] Logout API error:", err);
  }

  // The user explicitly confirmed discarding pending account uploads, if any.
  discardQuotaPending();

  // Clear auth state
  currentUser = null;
  if (masterKey instanceof Uint8Array) masterKey.fill(0);
  masterKey = null;
  e2eeEnvelope = null;
  clearStoredMasterKey();
  clearCachedUserId();

  // Clear vault storage cache
  if (VaultStorage && VaultStorage.clear) {
    VaultStorage.clear();
  }

  // Notify sibling tabs (same origin) that this user signed out. Receivers
  // show a non-destructive banner only — they DO NOT wipe IndexedDB or
  // tear down state on this signal, since a hostile in-page script could
  // forge it. We only broadcast when the server confirmed the logout, to
  // avoid nudging siblings on transient network errors.
  if (serverConfirmed) {
    try {
      const { broadcastLogout } = await import("../core/cross-tab-session.js");
      broadcastLogout();
    } catch (err) {
      console.warn("[Auth] broadcastLogout unavailable:", err);
    }
  }

  // Trigger logout callback
  if (onLogout) {
    await onLogout(cacheScope);
  }
  signingOutHere = false;
  // Signed out here, but the server was not reached: the other devices are
  // still signed in, and must be told so.
  if (everywhere && !serverConfirmed) {
    notify({ code: NOTICE.SIGNED_OUT_HERE_ONLY, level: 'warning', message: t('account.logoutAll.notReached'), duration: 10000 });
  }
}

/**
 * Check if user has active session
 */
export async function checkSession() {
  try {
    const user = await api("/api/auth/me");
    return user;
  } catch (err) {
    return null;
  }
}

/**
 * Check if server is reachable by pinging health endpoint
 * @returns {Promise<boolean>} true if server returns { status: "ok" }, false otherwise
 */
async function checkServerHealth() {
  if (!isSyncAvailable()) {
    // Local-only runtime: there is no server to probe.
    return false;
  }
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);

    const response = await fetch('/health', {
      method: 'GET',
      signal: controller.signal,
    });

    clearTimeout(timeout);

    if (!response.ok) return false;

    const data = await response.json();
    return data.status === 'ok';
  } catch {
    // Any error = server unavailable
    return false;
  }
}

/**
 * Try to restore session from stored master key
 * Uses quick health check to detect server-down scenarios
 * NEVER clears master key on transient errors - only explicit logout does that
 * Returns: { success: true } | { success: false, sessionExpired: boolean, serverDown?: boolean }
 */
export async function tryRestoreSession() {
  // Check for stored master key
  let storedKey = retrieveMasterKey();
  let fromDevice = false;
  const cachedIdAtStart = getCachedUserId();
  if (!storedKey) {
    // Nothing in sessionStorage. That is the normal state after a browser
    // restart — the server session cookie can still be valid for up to 30 days,
    // so before falling back to the password form, try the device key.
    //
    // Pass the cached user id so a stored key belonging to a different account
    // is rejected rather than restored. It is only a hint (it lives in
    // sessionStorage and is usually gone by now); the id comparison against
    // /auth/me below is the check that actually holds.
    fromDevice = await rehydrateMasterKeyFromDevice(cachedIdAtStart);
    storedKey = retrieveMasterKey();
  }
  if (!storedKey) {
    // No stored key found
    return { success: false, sessionExpired: false };
  }

  // Quick health check first - if server is down, skip auth attempts
  console.log("[Auth] Checking server health...");
  const serverOnline = await checkServerHealth();
  if (!serverOnline) {
    console.log("[Auth] Server unreachable, entering offline mode immediately");
    sessionExpired = true;
    return { success: false, sessionExpired: true, serverDown: true };
  }

  // Server is reachable, try to restore session
  try {
    const user = await api("/api/auth/me");
    if (!user) {
      // Server responded but no valid session - session truly expired
      console.log("[Auth] No valid session (server confirmed)");
      sessionExpired = true;
      return {
        success: false,
        sessionExpired: true,
        revoked: discardDeviceKeyOnRevocation(fromDevice),
      };
    }

    // Fetch envelope to verify key works
    const envelope = await api("/api/e2ee/envelope");
    if (!envelope) {
      // No envelope but session valid - unusual state, treat as expired
      console.log("[Auth] No envelope found");
      sessionExpired = true;
      return { success: false, sessionExpired: true };
    }

    // The stored key belongs to a different account than the session cookie.
    // Reachable when a second account signs in on this browser: the cookie is
    // for user B while the device still holds A's key. Never accept it.
    const restoredFromDevice = !!fromDevice;
    if (restoredFromDevice && cachedIdAtStart && cachedIdAtStart !== user.user_id) {
      console.warn("[Auth] Device key belongs to another account — discarding");
      clearStoredMasterKey();
      return { success: false, sessionExpired: false };
    }

    // Success! Restore state
    currentUser = user;
    masterKey = storedKey;
    e2eeEnvelope = envelope;
    sessionExpired = false;

    console.log("[Auth] Session restored for:", user.username);

    // Cache user ID for session expiry recovery
    cacheUserId(currentUser.user_id, currentUser.username);

    // Trigger success callback
    if (onAuthSuccess) {
      try {
        await onAuthSuccess({ user: currentUser, masterKey, e2eeEnvelope });
      } catch (bootErr) {
        // Nothing above proves the key still decrypts this account's data —
        // the envelope is only checked for existence, because verifying a
        // wrapped master key needs the password or recovery phrase. The vault
        // decrypt inside onAuthSuccess is the real test.
        //
        // Only a device-restored key gets torn down here. A key that came from
        // sessionStorage was proven by the password login that put it there, so
        // a failure at this point is some other boot problem and must keep its
        // existing behaviour rather than silently signing the user out.
        if (restoredFromDevice) {
          console.error("[Auth] Device-restored key failed to open the vault:", bootErr);
          currentUser = null;
          masterKey = null;
          e2eeEnvelope = null;
          clearStoredMasterKey();
          return { success: false, sessionExpired: false };
        }
        throw bootErr;
      }
    }

    return { success: true };
  } catch (err) {
    // Check if it's a confirmed 401 (not a network/parse error)
    if (err.status === 401) {
      console.log("[Auth] Session expired (401 confirmed)");
      sessionExpired = true;
      return {
        success: false,
        sessionExpired: true,
        revoked: discardDeviceKeyOnRevocation(fromDevice),
      };
    }

    // Network error after health check passed - server might have gone down
    console.log("[Auth] Auth request failed:", err.message);
    sessionExpired = true;
    return { success: false, sessionExpired: true, serverDown: true };
  }
}

/**
 * A server-confirmed 401 during restore means this device's session is gone —
 * it expired, or another device signed it out (a password change with "sign out
 * other devices", an account recovery, a deletion).
 *
 * When the key we were about to use came from the device store, drop it. That
 * is what makes remote sign-out actually enforceable: without it the device
 * would fall straight into offline mode still holding the master key, and could
 * keep opening cached documents on every subsequent boot. Wiping the key sends
 * the next boot to the password form instead.
 *
 * Only local *key material* is discarded — never documents. The encrypted CRDT
 * data stays exactly where it is, and signing back in with the new password
 * (the master key itself is unchanged by a password change) restores access to
 * all of it, including anything edited offline and not yet synced.
 *
 * A key that came from sessionStorage is left alone: it was put there by a
 * password login in this same browser session, and the pre-existing offline
 * mode built on it is a deliberate feature for a server that has gone away.
 *
 * @param {boolean} fromDevice - whether the key came from the device store
 * @returns {boolean} true if the device key was discarded
 */
function discardDeviceKeyOnRevocation(fromDevice) {
  if (!fromDevice) return false;
  console.warn("[Auth] Session revoked server-side — discarding stored device key");
  clearStoredMasterKey();
  currentUser = null;
  masterKey = null;
  e2eeEnvelope = null;
  return true;
}

/**
 * Get current user
 */
export function getCurrentUser() {
  return currentUser;
}

/**
 * Get master key
 */
export function getMasterKey() {
  return masterKey;
}

/**
 * Get E2EE envelope
 */
export function getE2eeEnvelope() {
  return e2eeEnvelope;
}

/**
 * Check if session is expired (user needs to re-authenticate)
 */
export function isSessionExpired() {
  return sessionExpired;
}

/** Whether this device is signing out (see `signingOutHere`). */
export function isSigningOutHere() {
  return signingOutHere;
}

/**
 * Set session expired state
 */
export function setSessionExpired(expired) {
  sessionExpired = expired;
}

